import {
  TextField as AriaTextField,
  TextFieldProps as AriaTextFieldProps,
  ValidationResult,
} from 'react-aria-components'
import { tv } from 'tailwind-variants'
import { Description, FieldError, Input, Label, TextArea, fieldBorderStyles } from './Field'
import { composeTailwindRenderProps, focusRing } from './utils'

const inputStyles = tv({
  extend: focusRing,
  base: 'border-2 rounded-md',
  variants: {
    isFocused: fieldBorderStyles.variants.isFocusWithin,
    ...fieldBorderStyles.variants,
  },
})

export interface TextFieldProps extends AriaTextFieldProps {
  label?: string,
  description?: string,
  errorMessage?: string | ((validation: ValidationResult) => string),
}

export function TextField(
  { label, description, errorMessage, ...props }: TextFieldProps,
) {
  return (
    <AriaTextField {...props} className={composeTailwindRenderProps(props.className, 'flex flex-col gap-1')}>
      {label && <Label>{label}</Label>}
      <Input className={inputStyles} />
      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
    </AriaTextField>
  )
}

const textAreaStyles = tv({
  extend: focusRing,
  base: 'border-2 rounded-md',
  variants: {
    isFocused: fieldBorderStyles.variants.isFocusWithin,
    ...fieldBorderStyles.variants,
    resize: {
      none: 'resize-none',
      vertical: 'resize-y',
      horizontal: 'resize-x',
      both: 'resize',
    },
    defaultVariants: {
      resize: 'vertical',
    },
  },
})

export interface TextAreaFieldProps extends TextFieldProps {
  rows?: number,
  cols?: number,
  label?: string,
  resize?: 'none' | 'vertical' | 'horizontal' | 'both',
  description?: string,
  errorMessage?: string | ((validation: ValidationResult) => string),
}

export function TextAreaField(
  { label, description, errorMessage, resize, rows, cols, value, onChange, ...props }: TextAreaFieldProps,
) {
  return (
    <AriaTextField {...props} className={composeTailwindRenderProps(props.className, 'flex flex-col gap-1')}>
      {label && <Label>{label}</Label>}
      <TextArea
        className={textAreaStyles({ resize })}
        value={value}
        onChange={ onChange ? e =>  onChange(e.target.value) : undefined}
        rows={rows}
        cols={cols}
      />
      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
    </AriaTextField>
  )
}
